import React, { createContext, useState, useContext, useEffect } from 'react';
import { Student } from '../types';
import { authAPI, studentAPI } from '../services/api';
import { isFirebaseAuthEnabled } from '../config/firebase';
import { captureAcquisition, loadStoredAcquisition, clearAcquisition } from '../hooks/useAcquisitionTracking';

// Auth provider type
type AuthProviderType = 'legacy' | 'firebase';

interface AuthContextType {
  student: Student | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  canFlagContent: boolean;
  isLoading: boolean;
  authProvider: AuthProviderType;
  // Legacy auth methods
  login: (phoneNumber: string, verificationCode: string) => Promise<void>;
  logout: () => void;
  sendOTP: (phoneNumber: string) => Promise<string>;
  register: (data: any) => Promise<void>;
  // Firebase auth method
  loginWithFirebaseToken: (accessToken: string, studentId: string) => Promise<void>;
  // Refresh student profile
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [student, setStudent] = useState<Student | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Determine auth provider from environment
  const authProvider: AuthProviderType = isFirebaseAuthEnabled() ? 'firebase' : 'legacy';

  // Capture first-touch acquisition on every mount. The hook itself
  // guarantees first-write-wins (it reads localStorage before writing),
  // so calling this on every page load is safe and ensures we don't
  // miss a tagged URL just because the user landed on a deep link.
  useEffect(() => {
    captureAcquisition();
  }, []);

  useEffect(() => {
    // Check if user is already logged in, proactively refresh token if near expiry
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          // Proactively refresh if token expires within 5 minutes
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              const expiresIn = (payload.exp * 1000) - Date.now();
              if (expiresIn < 5 * 60 * 1000) {
                // Token expires soon — refresh it
                const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/auth/refresh`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ refresh_token: refreshToken }),
                });
                if (response.ok) {
                  const data = await response.json();
                  localStorage.setItem('access_token', data.access_token);
                }
              }
            } catch {
              // Token decode failed — let the profile call handle it
            }
          }

          const profile = await studentAPI.getProfile();
          setStudent(profile.student);
        } catch (error: any) {
          // Don't clear auth on a ban — the axios interceptor has already
          // routed to /banned and the user needs the token to read the
          // detail. Only clear when the token itself is invalid (401).
          const status = error?.response?.status;
          const detail = error?.response?.data?.detail;
          const isBan = status === 403 && typeof detail === 'object' && detail?.code === 'account_banned';
          if (!isBan) {
            // Token invalid and refresh failed, clear storage
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('student_id');
          }
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  // Legacy: Send OTP via backend
  const sendOTP = async (phoneNumber: string): Promise<string> => {
    const response = await authAPI.sendOTP(phoneNumber);
    if (response.verification_id) {
      return response.verification_id;
    }
    throw new Error('Failed to send OTP');
  };

  // Legacy: Verify OTP via backend
  const login = async (phoneNumber: string, verificationCode: string) => {
    const response = await authAPI.verifyOTP(phoneNumber, verificationCode);

    if (response.access_token) {
      // Fetch student profile
      const profile = await studentAPI.getProfile();
      setStudent(profile.student);
    }
  };

  // Firebase: Login with pre-verified token
  const loginWithFirebaseToken = async (accessToken: string, studentId: string) => {
    // Token is already stored by useFirebaseAuth hook
    // Just fetch the profile
    try {
      const profile = await studentAPI.getProfile();
      setStudent(profile.student);
    } catch (error) {
      console.error('Failed to fetch profile after Firebase login:', error);
      throw error;
    }
  };

  const register = async (data: any) => {
    // Splice in any first-touch acquisition we captured earlier so the API
    // can record it on the student row. Fields the caller already set win
    // over stored values — but in practice Register.tsx never sets these,
    // they come exclusively from this hook.
    const stored = loadStoredAcquisition();
    const payload = {
      ...data,
      acquisition_source: data.acquisition_source ?? stored?.source ?? null,
      acquisition_campaign: data.acquisition_campaign ?? stored?.campaign ?? null,
      acquisition_payload: data.acquisition_payload ?? stored?.raw_query ?? null,
      referral_code: data.referral_code ?? stored?.referral_code ?? null,
    };
    const response = await authAPI.register(payload);
    // Once the row is created with attribution, drop the local copy so a
    // shared device doesn't carry it onto the next signup.
    clearAcquisition();
    // Refresh student profile after registration
    const profile = await studentAPI.getProfile();
    setStudent(profile.student);
  };

  const refreshProfile = async () => {
    try {
      const profile = await studentAPI.getProfile();
      setStudent(profile.student);
    } catch (error) {
      console.error('Failed to refresh profile:', error);
    }
  };

  const logout = () => {
    authAPI.logout();
    setStudent(null);
  };

  const value = {
    student,
    isAuthenticated: !!student,
    isAdmin: !!student?.roles?.includes('admin'),
    // Prefer the server-resolved capability (reflects PUBLIC_CONTENT_FLAGGING so
    // the 🚩 Report button reaches everyone when the crowd flag is on). Fall back
    // to the role check for older API builds that don't return can_flag_content.
    canFlagContent:
      student?.can_flag_content ??
      !!student?.roles?.some(r => r === 'admin' || r === 'reviewer'),
    isLoading,
    authProvider,
    login,
    logout,
    sendOTP,
    register,
    loginWithFirebaseToken,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
